import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaExternalLinkAlt } from "react-icons/fa";
import AppWrap from "../../wrapper/AppWrap";

const works = [
  {
    title: "Vibely",
    description:
      "Wellness booking platform for Nepal - built a fast, mobile-first experience with secure scheduling and discounts.",
    projectLink: "https://www.vibely.life",
    codeLink: "https://github.com/nishanneupane/vibely-frontend",
    imageUrl:
      "https://www.upwork.com/att/download/portfolio/persons/uid/1659533540401610752/profile/projects/files/700124e8-f98f-4288-9151-28e354a8690e",
    tags: ["SaaS", "Bookings"],
  },
  {
    title: "Verboid AI Platform",
    description: "Mock interview and English-speaking practice platform powered by AI and LLMs.",
    projectLink: "https://verboid.com/",
    codeLink: "#",
    imageUrl: "https://mockwise.vercel.app/_next/image?url=%2Flogo.png&w=256&q=75",
    tags: ["AI", "Education"],
  },
  {
    title: "Pahuna Homes",
    description:
      "Tourism marketplace for authentic local stays across Nepal. Implemented booking flows and API integrations.",
    projectLink: "https://pahunahomes.com/",
    codeLink: "#",
    imageUrl: "https://www.pahunahomes.com/images/logo.svg",
    tags: ["Marketplace", "Travel"],
  },
  {
    title: "HR Home Tuition",
    description: "Platform connecting students with vetted home tutors; built end-to-end onboarding and search.",
    projectLink: "https://www.hrhometuition.com",
    codeLink: "#",
    imageUrl: "https://www.hrhometuition.com/images/hrlogo.png",
    tags: ["Platform", "Education"],
  },
  {
    title: "Expert HR Outsourcing",
    description: "Software solution suite for HR services with secure workflows and dashboards.",
    projectLink: "https://www.experthroutsourcing.com",
    codeLink: "#",
    imageUrl: "https://www.experthroutsourcing.com/_next/image?url=%2Fimages%2Flogo.png&w=1920&q=75",
    tags: ["B2B", "Dashboards"],
  },
  {
    title: "Educational Consultancy",
    description: "Guidance platform for international education seekers with lead capture and booking.",
    projectLink: "https://genconsultancy.vercel.app/",
    codeLink: "#",
    imageUrl: "https://genconsultancy.vercel.app/images/hero/diverse-nepali-students-studying.jpg",
    tags: ["Lead-gen", "Services"],
  },
  {
    title: "Harash Tattoo",
    description: "Custom tattoo studio site with gallery, booking prompts, and bold visual direction.",
    projectLink: "https://harashtattoo.com/",
    codeLink: "#",
    imageUrl: "https://harashtattoo.com/wp-content/uploads/2024/11/D7D1D2EE-EF46-4750-8D0F-DC24AF7DABA6-1229x1536.jpg",
    tags: ["Brand", "Ecommerce"],
  },
  {
    title: "Menzz",
    description: "Menswear ecommerce experience with modern UI, conversion-focused flows, and quick checkouts.",
    projectLink: "https://menzz.co",
    codeLink: "#",
    imageUrl:
      "https://www.upwork.com/att/download/portfolio/persons/uid/1659533540401610752/profile/projects/files/3800a624-b980-49df-b82d-d558ca769449",
    tags: ["Ecommerce"],
  },
  {
    title: "Deliveroo Web Design",
    description: "Responsive concept for Deliveroo with micro-interactions and content blocks.",
    projectLink: "https://deliveroonp.vercel.app/",
    codeLink: "#",
    imageUrl: "https://deliveroonp.vercel.app/_next/image?url=%2Fdeliveroo_ui%2Fhero-1.jpg&w=3840&q=75",
    tags: ["UI Design", "Clone"],
  },
  {
    title: "Local Project MIS",
    description: "Management information system for local government projects with tracking and transparency.",
    projectLink: "#",
    codeLink: "#",
    imageUrl: "https://upload.wikimedia.org/wikipedia/commons/2/23/Emblem_of_Nepal.svg",
    tags: ["GovTech"],
  },
];

const Work = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTag, setActiveTag] = useState("All");

  const tags = useMemo(() => ["All", ...new Set(works.flatMap((w) => w.tags))], []);

  const filteredWorks = works.filter((work) => {
    const matchesSearch =
      work.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      work.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTag = activeTag === "All" || work.tags.includes(activeTag);
    return matchesSearch && matchesTag;
  });

  return (
    <section className="space-y-10">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm uppercase tracking-[0.3em] text-primary">Selected projects</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white">Recent work that ships and scales.</h2>
          <p className="text-slate-300 max-w-2xl mt-2">
            Product strategy, design systems, and fast engineering - tailored for startups and established teams alike.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:block px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-slate-200">
            {filteredWorks.length} projects
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <button
              key={tag}
              onClick={() => setActiveTag(tag)}
              className={`px-3 py-2 rounded-full text-sm border ${activeTag === tag
                ? "bg-white/15 border-white/30 text-white shadow-glow"
                : "bg-white/5 border-white/10 text-slate-200 hover:border-white/25"
                }`}
            >
              {tag}
            </button>
          ))}
        </div>

        <div className="w-full lg:w-72 relative">
          <input
            type="text"
            placeholder="Search by title or description"
            className="w-full px-4 py-3 rounded-full bg-white/5 border border-white/10 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-primaryDark/70"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <AnimatePresence>
        <motion.div layout className="grid gap-7 md:grid-cols-2 xl:grid-cols-3">
          {filteredWorks.map((work, index) => (
            <motion.article
              key={work.title}
              layout
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl"
            >
              <div className="relative h-48">
                <img src={work.imageUrl} alt={work.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/20 to-transparent" />
                <div className="absolute top-3 left-3 flex flex-wrap gap-2">
                  {work.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.12em] bg-black/60 text-white border border-white/15">
                      {tag}
                    </span>
                  ))}
                </div>
                <a
                  href={work.projectLink}
                  target="_blank"
                  rel="noreferrer"
                  className="absolute bottom-3 right-3 flex items-center gap-2 px-3 py-2 rounded-full bg-white/10 text-sm text-white border border-white/20 backdrop-blur hover:bg-white/20 transition-colors"
                >
                  View live <FaExternalLinkAlt size={12} />
                </a>
              </div>

              <div className="p-6 space-y-3">
                <h3 className="text-xl font-semibold text-white">{work.title}</h3>
                <p className="text-slate-300 text-sm leading-relaxed">{work.description}</p>

              </div>
            </motion.article>
          ))}
        </motion.div>
      </AnimatePresence>
    </section>
  );
};

export default AppWrap(Work, "work");
